import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { apiFetch } from "../../api/apiFetch";

const categories = [
  { value: "confusing", label: "Confusing" },
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "account", label: "Account" },
  { value: "other", label: "Other" },
];

export default function FeedbackDialog({ open, onClose, surface = "web" }) {
  const [category, setCategory] = React.useState("confusing");
  const [message, setMessage] = React.useState("");
  const [contactOk, setContactOk] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setCategory("confusing");
      setError("");
      setSent(false);
      return;
    }
    setMessage("");
    setContactOk(true);
    setSubmitting(false);
    setError("");
    setSent(false);
  }, [open]);

  function close() {
    if (!submitting) {
      onClose?.();
    }
  }

  async function handleSubmit() {
    const trimmed = message.trim();
    if (!trimmed) {
      setError("Write one sentence before sending.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await apiFetch("/feedback/", {
        method: "POST",
        body: JSON.stringify({
          category,
          message: trimmed,
          surface,
          contact_ok: contactOk,
          app_platform: "web",
          metadata: {
            path: window.location.pathname,
            user_agent: window.navigator?.userAgent?.slice(0, 160) || "",
          },
        }),
      });
      setMessage("");
      setSent(true);
    } catch (err) {
      setError(err?.message || "Unable to send feedback right now.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleEmailFallback() {
    const params = new URLSearchParams({
      subject: "TrustBud Feedback",
      body: `Category: ${category}\nScreen: ${surface}\nContact ok: ${contactOk ? "yes" : "no"}\n\n${message.trim()}`,
    });
    window.location.href = `mailto:support@trustbud.app?${params.toString()}`;
  }

  const canSubmit = Boolean(message.trim()) && !submitting;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: "1.2rem", fontWeight: 900, color: "#13233d" }}>
          Send feedback
        </Typography>
        <Typography sx={{ color: "#61718a", fontSize: "0.92rem", mt: 0.5 }}>
          Tell us what felt off, broken, or worth improving.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {sent ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Feedback sent. It is saved with enough context for review.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <ToggleButtonGroup
              value={category}
              exclusive
              size="small"
              onChange={(_, value) => value && setCategory(value)}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              {categories.map((item) => (
                <ToggleButton
                  key={item.value}
                  value={item.value}
                  sx={{
                    borderRadius: "999px !important",
                    border: "1px solid #dce6f1 !important",
                    px: 1.5,
                    py: 0.7,
                    fontWeight: 800,
                    color: "#52627a",
                    "&.Mui-selected": {
                      color: "#1050b0",
                      backgroundColor: "#e8f3ff",
                    },
                  }}
                >
                  {item.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                if (error) setError("");
              }}
              label="What should feel better?"
              multiline
              minRows={5}
              inputProps={{ maxLength: 1200 }}
              disabled={submitting}
              helperText={`${message.length}/1200`}
              fullWidth
            />

            <Box
              sx={{
                border: "1px solid #dce6f1",
                borderRadius: 2,
                px: 1.5,
                py: 1,
                backgroundColor: "#f7fbff",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={contactOk}
                    onChange={(event) => setContactOk(event.target.checked)}
                    disabled={submitting}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 850, color: "#13233d", fontSize: "0.92rem" }}>
                      Support can follow up
                    </Typography>
                    <Typography sx={{ color: "#61718a", fontSize: "0.82rem" }}>
                      Turn this off if you only want to leave a note.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {error ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={handleEmailFallback}>
                    Email instead
                  </Button>
                }
              >
                {error}
              </Alert>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} disabled={submitting}>
          {sent ? "Done" : "Cancel"}
        </Button>
        {!sent ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Send feedback
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
